import React from 'react';
import I18n from 'i18n-js';
import * as elog from '@reverbdotcom/commons/src/elog';
import gql from 'graphql-tag';
import { connect } from '@reverbdotcom/commons/src/connect';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import Modal from '@reverbdotcom/commons/src/components/modal';
import { useMutation, MutationResult } from '@reverbdotcom/commons/src/useMutation';
import { MutationFn } from 'react-apollo';
import { UpsertMyFavorite, CoreFindFavoriteProviderUpsertMyFavorite, DeleteMyFavorite } from '../../schemas/rql';
import { withRouter, WithRouterProps } from 'react-router';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import classNames from 'classnames';
import { RCButton, RCSwitch } from '@reverbdotcom/cadence/components';
import { DeleteMyFavoriteMutationFn, useDeleteMyFavorite, withDeleteMyFavoriteMutation } from './delete_my_favorite_mutation';

interface ExternalProps {
  isOpen: boolean;
  handleClose: Function;
  handleUpdate: Function;
  modalTitle: string;
  searchTitle: string;
  feedEnabled: boolean;
  emailEnabled: boolean;
  queryParams: string;
  subtitle?: string;
  handleMutationResult?: (result: MutationResult<UpsertMyFavorite.Mutation>) => void;
  withRemove?: boolean;
  handleRemove?: (result: MutationResult<DeleteMyFavorite.Mutation>) => void;
  favoriteId?: string;
  refetchQueryName?: string;
}

interface ApolloProps {
  upsertMyFavorite: MutationFn<UpsertMyFavorite.Mutation, UpsertMyFavorite.Variables> | MutationFn<CoreFindFavoriteProviderUpsertMyFavorite.Mutation, CoreFindFavoriteProviderUpsertMyFavorite.Variables>;
  deleteMyFavorite: DeleteMyFavoriteMutationFn;
}

type IProps = ExternalProps & ApolloProps & WithRouterProps;

export function FavoriteModal(props: IProps) {
  const [upsertFavorite] = useMutation(props.upsertMyFavorite);
  const { deleteMyFavorite } = useDeleteMyFavorite(props.deleteMyFavorite, props.favoriteId, props.refetchQueryName);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await upsertFavorite({
        variables: {
          input: {
            queryParams: props.queryParams,
            feedEnabled: props.feedEnabled,
            emailEnabled: props.emailEnabled,
          },
        },
      });

      if (props.handleMutationResult) {
        props.handleMutationResult(result);
      }

      props.handleClose();
    } catch (e) {
      elog.error(
        e.message,
        { componentName: 'FavoriteSearchesModal' },
        e,
      );
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    try {
      const result = await deleteMyFavorite();
      props.handleClose();
      props.handleRemove(result);
    } catch (e) {
      elog.error(
        e.message,
        { componentName: 'FavoriteModal' },
        e,
      );
    }
  };

  const handleClose = (e) => {
    if (e) { e.preventDefault(); }
    props.handleClose();
  };

  const favoritesShoppabilityEnabled = useExpEnabled(experiments.FAVORITES_SHOPPABILITY);

  const renderSubtitle = () => {
    if (favoritesShoppabilityEnabled) {
      return (
        props.searchTitle &&
          <div className="favorite-modal__header__shoppability-follow-title">
            {I18n.t('discovery.favorites.confirmationModal.firstToKnowSubtitle', { title: props.searchTitle })}
          </div>
      );
    } else {
      return (
        props.searchTitle &&
          <div className="favorite-modal__header__follow-title">
            {I18n.t('discovery.favorites.confirmationModal.subtitle', { title: props.searchTitle })}
          </div>
      );
    }
  };

  const headerClass = classNames(
    { 'favorite-modal__header': !favoritesShoppabilityEnabled },
    { 'favorite-modal__shoppability-header': favoritesShoppabilityEnabled },
  );

  const filtersLabel = props.subtitle ? I18n.t('discovery.favorites.confirmationModal.filters') : I18n.t('discovery.favorites.confirmationModal.noFilters');

  return (
    <Modal
      isOpen={props.isOpen}
      subClass="feed"
      onRequestClose={handleClose}
    >
      <div className={headerClass}>
        <h1>{props.modalTitle}</h1>
        {renderSubtitle()}
      </div>

      {favoritesShoppabilityEnabled ? (
        <>
          <div className="favorite-modal__shoppability-subsection">
            <h1>{filtersLabel}</h1>
            <div className="favorite-modal__header__shoppability-text">
              {props.subtitle}
            </div>
          </div>
          <div className="favorite-modal__shoppability-subsection">
            <h1>{I18n.t('discovery.favorites.confirmationModal.savedSearchFeed')}</h1>

            <div className="favorite-modal__option">
              <RCSwitch
                checked={props.feedEnabled}
                id="add-to-feed-alert-toggle"
                label={I18n.t('discovery.favorites.confirmationModal.addMatchingItemsToFeed')}
                onChange={(e) => props.handleUpdate('feedEnabled', e.target.checked)}
              />
              {props.feedEnabled &&
                <RCSwitch
                  checked={props.emailEnabled}
                  id="add-to-email-alert-toggle"
                  label={I18n.t('discovery.favorites.confirmationModal.addToYourDailyEmail')}
                  onChange={e => props.handleUpdate('emailEnabled', e.target.checked)}
                />
              }
            </div>
          </div>
        </>
      ) : (
        <div className="favorite-modal__option">
          <div className="favorite-modal__switch">
            <input
              type="checkbox"
              id="add-to-feed-alert-toggle"
              onChange={(e) => props.handleUpdate('feedEnabled', e.target.checked)}
              checked={props.feedEnabled}
              aria-checked={props.feedEnabled}
            />
            <label htmlFor="add-to-feed-alert-toggle">
              {I18n.t('discovery.favorites.confirmationModal.addToFeed')}
            </label>
          </div>
          {props.feedEnabled &&
            <div className="favorite-modal__switch">
              <input
                type="checkbox"
                id="add-to-email-alert-toggle"
                onChange={e => props.handleUpdate('emailEnabled', e.target.checked)}
                checked={props.emailEnabled}
                aria-checked={props.emailEnabled}
              />
              <label htmlFor="add-to-email-alert-toggle">
                {I18n.t('discovery.favorites.confirmationModal.addToDailyEmail')}
              </label>
            </div>
          }
        </div>
      )}

      {favoritesShoppabilityEnabled ? (
        <div className="favorite-modal__actions">
          <div className="favorite-modal__save-and-cancel-buttons">
            <RCButton
              variant="primary"
              onClick={e => handleSubmit(e)}
            >
              {I18n.t('discovery.favorites.confirmationModal.save')}
            </RCButton>

            <div className="mobile-d-none">
              <RCButton
                variant="secondary"
                onClick={() => props.handleClose()}
              >
                {I18n.t('discovery.favorites.confirmationModal.cancel')}
              </RCButton>
            </div>
          </div>
          
          {props.withRemove &&
            <RCButton
              variant="transparent"
              theme="danger"
              onClick={e => handleDelete(e)}
            >
              {I18n.t('discovery.favorites.confirmationModal.remove')}
            </RCButton>
          }
        </div>
      ) : (
        <div className="favorite-modal__action">
          <button id="submit-follow" type="button" onClick={e => handleSubmit(e)}>
            {I18n.t('discovery.favorites.confirmationModal.submit')}
          </button>
        </div>
      )}
    </Modal>
  );
}

/**
 * One mutation and connected component for the favorites hub (modifying settings for existing favorites)
 */
export const FAVORITES_HUB_UPSERT_FAVORITE_MUTATION = gql`
  mutation UpsertMyFavorite($input: Input_core_apimessages_UpsertMyFavoriteRequest) {
    upsertMyFavorite(input: $input) {
      favorite {
        id
        favorited
        emailEnabled
        feedEnabled
        searchableId
        searchableType
        queryParams
      }
    }
  }
`;

const withFavoritesHubUpsertFavoriteMutation =
  withGraphql<ExternalProps, UpsertMyFavorite.Mutation, UpsertMyFavorite.Variables>(
    FAVORITES_HUB_UPSERT_FAVORITE_MUTATION,
    {
      name: 'upsertMyFavorite',
    },
  );

export const FavoritesHubUpsertFavoriteModal = connect<ExternalProps>([
  withRouter,
  withFavoritesHubUpsertFavoriteMutation,
  withDeleteMyFavoriteMutation,
])(FavoriteModal);

/**
 * One mutation and connected component for the FindFavoriteProvider (saving a new favorite from marketplace pages or CSPs)
 */
export const FIND_FAVORITE_PROVIDER_UPSERT_FAVORITE_MUTATION = gql`
  mutation Core_FindFavoriteProvider_UpsertMyFavorite($input: Input_core_apimessages_UpsertMyFavoriteRequest) {
    upsertMyFavorite(input: $input) {
      favorite {
        id
        favorited
        emailEnabled
        feedEnabled
        searchableId
        searchableType
        queryParams
      }
    }
  }
`;

const withFindFavoriteProviderUpsertFavoriteMutation =
  withGraphql<ExternalProps, CoreFindFavoriteProviderUpsertMyFavorite.Mutation, CoreFindFavoriteProviderUpsertMyFavorite.Variables>(
    FIND_FAVORITE_PROVIDER_UPSERT_FAVORITE_MUTATION,
    {
      name: 'upsertMyFavorite',
    },
  );

export const FindFavoriteProviderUpsertFavoriteModal = connect<ExternalProps>([
  withRouter,
  withFindFavoriteProviderUpsertFavoriteMutation,
])(FavoriteModal);
